import React from 'react';
import { Printer, RefreshCw, FileText } from 'lucide-react';
import { fakeBackendPayload } from '../../../../tests/qztray/fakeBackendPayload';
import { BackendQzPayloadAdapter } from '../../../../utils/qztray/BackendQzPayloadAdapter';

interface Props {
  connectClient: Function;
  discoverPrinters: Function;
  print: Function;
  isConnected: boolean;
  printer: string;
  setPrinter: (p: string) => void;
  setPrinters: (list: string[]) => void;
  printers: string[];
  isLoading: boolean;
  setIsLoading: (b: boolean) => void;
  rawTestPayload: any;
}

const PrintControlSection: React.FC<Props> = ({
  connectClient,
  discoverPrinters,
  print,
  isConnected,
  printer,
  setPrinter,
  setPrinters,
  isLoading,
  setIsLoading,
  rawTestPayload,
}) => {
  // 🔍 Buscar impresoras
  const handleDiscoverPrinters = async () => {
    try {
      setIsLoading(true);
      await connectClient();
      const list = await discoverPrinters();
      setPrinters(list);
      const rptPrinter = list.find((p) => p.includes('RPT004'));
      const selected = rptPrinter || list[0];
      setPrinter(selected);
    } catch (err) {
      console.error('❌ Error al descubrir impresoras:', err);
      alert('No se pudo obtener la lista de impresoras.');
    } finally {
      setIsLoading(false);
    }
  };

  // 🧾 Imprimir ticket manual
  const handlePrintRawTest = async () => {
    try {
      if (!printer) return alert('Seleccione una impresora primero.');
      await print(printer, rawTestPayload.commands);
      alert(`✅ Ticket impreso correctamente en ${printer}.`);
    } catch (err) {
      console.error('❌ Error al imprimir:', err);
      alert('Error al imprimir: ' + (err as Error).message);
    }
  };

  // 🧾 Imprimir payload backend SIM
  const handlePrintBackendSimulation = async () => {
    try {
      if (!printer) return alert('Seleccione una impresora primero.');

      const rawCommands =
        BackendQzPayloadAdapter.toRawEscPos(fakeBackendPayload.data);
        console.log(rawCommands);
      await print(printer, rawCommands);

      alert(`✅ Ticket backend SIMULADO impreso correctamente en ${printer}.`);
    } catch (err) {
      console.error('❌ Error al imprimir backend simulado:', err);
      alert('Error al imprimir backend simulado: ' + (err as Error).message);
    }
  };

  return (
    <section className="rounded-sm border border-stroke bg-white shadow-default transition dark:border-strokedark dark:bg-boxdark">
      {/* === Header === */}
      <div className="flex items-center gap-2 border-b border-stroke px-4 py-3 dark:border-strokedark sm:px-5 xl:px-6">
        <Printer className="h-5 w-5 text-primary" />
        <h3 className="font-semibold text-black dark:text-white">
          Control de Impresión
        </h3>
      </div>

      {/* === Body === */}
      <div className="p-4 sm:p-5 xl:p-6">
        <p className="text-gray-600 dark:text-gray-400 mb-4 text-sm">
          Estado:{' '}
          {isConnected ? (
            <span className="font-medium text-green-500">Conectado</span>
          ) : (
            <span className="text-red-500 font-medium">Desconectado</span>
          )}
        </p>

        {/* === Botones === */}
        <div className="flex flex-col gap-3">
          <button
            onClick={handleDiscoverPrinters}
            disabled={isLoading}
            className={`inline-flex items-center justify-center gap-2 rounded-md px-4 py-2 text-sm font-medium text-white transition
              ${isLoading ? 'bg-blue-400' : 'bg-primary hover:bg-primary/90'}
            `}
          >
            <RefreshCw className="h-4 w-4" />
            {isLoading ? 'Buscando...' : 'Buscar Impresoras'}
          </button>

          <button
            onClick={handlePrintRawTest}
            disabled={!printer}
            className={`inline-flex items-center justify-center gap-2 rounded-md px-4 py-2 text-sm font-medium text-white transition
              ${
                printer
                  ? 'bg-green-600 hover:bg-green-700'
                  : 'bg-gray-400 cursor-not-allowed opacity-60'
              }
            `}
          >
            <FileText className="h-4 w-4" />
            Imprimir Ticket RAW
          </button>

          <button
            onClick={handlePrintBackendSimulation}
            disabled={!printer}
            className={`inline-flex items-center justify-center gap-2 rounded-md px-4 py-2 text-sm font-medium text-white transition
              ${
                printer
                  ? 'bg-purple-600 hover:bg-purple-700'
                  : 'bg-gray-400 cursor-not-allowed opacity-60'
              }
            `}
          >
            <FileText className="h-4 w-4" />
            Imprimir Backend (Simulación)
          </button>
        </div>
      </div>
    </section>
  );
};

export default PrintControlSection;
